import { useState } from 'react';
import './PeerStatus.css';

interface PeerInfo {
  id: string;
  state: 'discovered' | 'connecting' | 'connected' | 'disconnected';
  lastSeen: number;
}

interface PeerStatusProps {
  peers: PeerInfo[];
  connectedCount: number;
  onConnect: (peerId: string) => void;
  onDisconnect: (peerId: string) => void;
  onAnnounce: () => void;
  redPillMode: boolean;
  isReady?: boolean;
}

type MeshStatus = 'healthy' | 'limited' | 'offline';

function getMeshStatus(connectedCount: number): MeshStatus {
  if (connectedCount >= 3) return 'healthy';
  if (connectedCount >= 1) return 'limited';
  return 'offline';
}

function getMeshLabel(status: MeshStatus, connectedCount: number): string {
  switch (status) {
    case 'healthy':
      return `${connectedCount} peers`;
    case 'limited':
      return `${connectedCount} peer${connectedCount > 1 ? 's' : ''}`;
    case 'offline':
      return 'Offline';
  }
}

function getMeshTooltip(status: MeshStatus): string {
  switch (status) {
    case 'healthy':
      return 'Healthy mesh connection';
    case 'limited':
      return 'Limited connectivity';
    case 'offline':
      return 'Working from local cache';
  }
}

export function PeerStatus({
  peers,
  connectedCount,
  onConnect,
  onDisconnect,
  onAnnounce,
  redPillMode,
  isReady = true,
}: PeerStatusProps) {
  const [expanded, setExpanded] = useState(false);
  const meshStatus = getMeshStatus(connectedCount);

  if (!redPillMode) {
    // Blue pill mode: minimal indicator with three states
    return (
      <div
        className="peer-status-minimal"
        title={getMeshTooltip(meshStatus)}
        onClick={() => setExpanded(!expanded)}
      >
        <span className={`status-dot mesh-${meshStatus}`} />
        <span className="peer-count">{getMeshLabel(meshStatus, connectedCount)}</span>
      </div>
    );
  }

  // Red pill mode: full details
  return (
    <div className="peer-status">
      <div className="peer-status-header" onClick={() => setExpanded(!expanded)}>
        <span className={`status-dot mesh-${meshStatus}`} title={getMeshTooltip(meshStatus)} />
        <span className="peer-count">
          {connectedCount}/{peers.length} peers
        </span>
        <span className={`mesh-badge mesh-badge-${meshStatus}`}>
          {meshStatus === 'healthy' ? 'MESH' : meshStatus === 'limited' ? 'LIMITED' : 'OFFLINE'}
        </span>
        {isReady && <span className="p2p-badge">P2P</span>}
        <button className="refresh-btn" onClick={(e) => { e.stopPropagation(); onAnnounce(); }}>
          Scan
        </button>
        <span className="expand-icon">{expanded ? '−' : '+'}</span>
      </div>

      {expanded && (
        <div className="peer-list">
          {peers.length === 0 ? (
            <div className="no-peers">
              No peers discovered. Open another tab to test P2P sync.
            </div>
          ) : (
            peers.map(peer => (
              <div key={peer.id} className={`peer-item state-${peer.state}`}>
                <span className="peer-id mono">{peer.id.slice(0, 12)}...</span>
                <span className={`peer-state ${peer.state}`}>{peer.state}</span>
                {peer.state === 'discovered' && (
                  <button className="connect-btn" onClick={() => onConnect(peer.id)}>
                    Connect
                  </button>
                )}
                {peer.state === 'connected' && (
                  <button className="disconnect-btn" onClick={() => onDisconnect(peer.id)}>
                    Disconnect
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
